'use client'

import { createContext, use, useActionState, useEffect, useState } from 'react'
import Link from 'next/link'

import { Button } from '@/core/components/form/button/button'
import { buttonClassName } from '@/core/components/form/button/button-styles'
import { Combobox } from '@/core/components/form/combobox'
import NumberStepper from '@/core/components/form/number-stepper'
import { toast } from '@/core/lib/toast'
import { cn } from '@/core/utils/common'
import { addToCart, type AddToCartState } from '@/features/cart/actions/cart'
import { formatWon, SHOP_POLICY } from '@/features/site/config'
import { useViewer } from '@/features/viewer/hooks/use-viewer'

import WishButton from './wish-button'

import type { Route } from 'next'
import type { ReactNode } from 'react'
import type { ProductOption } from '../schemas/catalog.schema'

type PurchaseInfo = {
  productId: string
  price: number
  options: ProductOption[]
  purchasable: boolean
  soldOut: boolean
}

type PurchaseContextValue = PurchaseInfo & {
  optionId: string
  setOptionId: (id: string) => void
  quantity: number
  setQuantity: (quantity: number) => void
  unitPrice: number
}

const PurchaseContext = createContext<PurchaseContextValue | null>(null)

function usePurchase() {
  const value = use(PurchaseContext)
  if (!value) throw new Error('PurchaseProvider가 필요합니다.')
  return value
}

export function PurchaseProvider({
  children,
  ...info
}: PurchaseInfo & { children: ReactNode }) {
  const [optionId, setOptionId] = useState('')
  const [quantity, setQuantity] = useState(1)
  const option = info.options.find((item) => item.id === optionId)
  return (
    <PurchaseContext
      value={{
        ...info,
        optionId,
        setOptionId,
        quantity,
        setQuantity,
        unitPrice: info.price + (option?.extraPrice ?? 0),
      }}
    >
      {children}
    </PurchaseContext>
  )
}

const INITIAL_STATE: AddToCartState = { status: 'idle' }

function optionLabel(option: ProductOption) {
  if (option.extraPrice === 0) return option.name
  const sign = option.extraPrice > 0 ? '+' : '-'
  return `${option.name} (${sign}${formatWon(Math.abs(option.extraPrice))})`
}

export function PurchasePanel({ variant }: { variant: 'full' | 'side' }) {
  const {
    productId,
    options,
    purchasable,
    soldOut,
    optionId,
    setOptionId,
    quantity,
    setQuantity,
    unitPrice,
  } = usePurchase()
  const [state, action, pending] = useActionState(addToCart, INITIAL_STATE)
  const { patch } = useViewer()
  const isSide = variant === 'side'

  useEffect(() => {
    if (state.status !== 'success') return
    if (state.message) toast.success(state.message)
    if (state.cartCount !== undefined) {
      void patch({ cartCount: state.cartCount })
    }
  }, [state, patch])

  const optionField = options.length > 0 && (
    <Combobox
      label="옵션"
      name="optionId"
      required
      placeholder="옵션을 선택해주세요"
      options={options.map((item) => ({
        value: item.id,
        label: optionLabel(item),
      }))}
      value={optionId}
      onChange={setOptionId}
    />
  )
  const quantityField = (
    <div className="flex items-center justify-between gap-4">
      <span className="type-ui font-medium">수량</span>
      <NumberStepper
        value={quantity}
        onChange={setQuantity}
        min={1}
        max={SHOP_POLICY.cartMaxQuantity}
        disabled={!purchasable}
      />
    </div>
  )
  const total = (
    <div className="flex items-baseline justify-between">
      <span className="type-ui font-medium">총 금액</span>
      <span className="text-c24 font-bold tabular-nums over10:text-c26">
        {formatWon(unitPrice * quantity)}
      </span>
    </div>
  )
  const messages = (
    <>
      {state.status === 'error' && (
        <p role="alert" className="type-meta text-error">
          {state.message}
        </p>
      )}
      {state.status === 'success' && (
        <p role="status" className="type-meta text-success">
          장바구니에 담았습니다.{' '}
          <Link href="/cart" className="underline">
            장바구니 보기
          </Link>
        </p>
      )}
    </>
  )

  return (
    <form
      action={action}
      id={isSide ? undefined : 'purchase'}
      className={cn(
        'flex flex-col gap-4',
        isSide
          ? 'rounded-panel border border-border p-5'
          : 'mt-5 scroll-mt-24 border-t border-border pt-5'
      )}
    >
      <input type="hidden" name="productId" value={productId} />
      <input type="hidden" name="quantity" value={quantity} />
      {optionField}
      {quantityField}
      <div className="border-t border-border pt-4">{total}</div>
      {isSide && (
        <p className="type-meta text-description">
          택배 {formatWon(SHOP_POLICY.shippingFee)}
          <br />
          제주·도서지역 {formatWon(SHOP_POLICY.remoteAreaFee)} 추가
        </p>
      )}
      {messages}
      {purchasable ? (
        isSide ? (
          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              name="intent"
              value="buy"
              size="lg"
              loading={pending}
            >
              바로 구매
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <WishButton productId={productId} labelled />
              <Button
                type="submit"
                name="intent"
                value="cart"
                variant="outline"
                loading={pending}
              >
                장바구니
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="submit"
              name="intent"
              value="cart"
              variant="outline"
              size="lg"
              loading={pending}
            >
              장바구니
            </Button>
            <Button
              type="submit"
              name="intent"
              value="buy"
              size="lg"
              loading={pending}
            >
              바로 구매
            </Button>
          </div>
        )
      ) : (
        <div className="grid gap-2">
          <Button type="button" size="lg" disabled>
            {soldOut ? '품절된 상품입니다' : '구매할 수 없는 상품입니다'}
          </Button>
          <Link
            href={'/support/inquiry' as Route}
            className={buttonClassName('outline', 'md')}
          >
            재입고 문의하기
          </Link>
        </div>
      )}
    </form>
  )
}

export function MobilePurchaseBar() {
  const { unitPrice, quantity, purchasable, soldOut } = usePurchase()
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-4 border-t border-border bg-background px-5 py-3 over10:hidden">
      <span className="text-c20 font-bold tabular-nums">
        {formatWon(unitPrice * quantity)}
      </span>
      <a href="#purchase" className={buttonClassName('primary', 'md')}>
        {purchasable ? '구매하기' : soldOut ? '품절' : '구매 불가'}
      </a>
    </div>
  )
}
